import { useState } from "react";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { useT } from "../i18n/index.js";
import { formatMoney } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";
import { readImportFileAsCsv } from "../lib/xlsx-to-csv.js";
import * as XLSX from "xlsx";

/**
 * F7.1 — Importação eDSP.
 *
 * Fluxo:
 *   1. Operador seleciona empresa
 *   2. Upload Arquivo 1 (dossiers) → preview → confirmar importDossiers
 *   3. Upload Arquivo 2 (encaissements) → preview → confirmar importReceipts
 *
 * Idempotente: re-import skip linhas com N° Devis (dossier) ou
 * EDSP:<virement>:<invoice_id> (receipt) já presentes.
 */

type DossierPreview = NonNullable<
  ReturnType<typeof trpc.edspImport.previewDossiers.useMutation>["data"]
>;

export default function ImportEdsp() {
  const t = useT();
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );
  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);

  // Mantém o conteúdo dos CSVs em estado para reuso entre preview e import.
  const [dossierCsv, setDossierCsv] = useState<string | null>(null);

  const [dossierPreview, setDossierPreview] = useState<DossierPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<string | null>(null);

  const previewDossiersMut = trpc.edspImport.previewDossiers.useMutation({
    onSuccess: (data) => {
      setDossierPreview(data);
      setError(null);
    },
    onError: (e) => setError(e.message),
  });
  const importDossiersMut = trpc.edspImport.importDossiers.useMutation({
    onSuccess: (data) => {
      let msg = `${t("importEdsp.dossiersImported")}: ${data.summary.imported} ${t("importEdsp.new")} · ${data.summary.skipped} ${t("importEdsp.alreadyExisted")} · ${data.summary.failed} ${t("importEdsp.failures")}.`;
      // F-receipts-100pct + xlsx — expõe erros por linha para o operador
      // ver a causa raiz (antes ficavam só em log do servidor).
      // F-import-full-warnings (pedido do CEO): lista COMPLETA — cortar em 5
      // escondia justamente os pares que faltava cadastrar. Cap defensivo em
      // 200 linhas (o painel rola).
      const CAP = 200;
      if (data.errors && data.errors.length > 0) {
        const sample = data.errors
          .slice(0, CAP)
          .map((e) => `  • L${e.lineNumber} (${e.numDevis}): ${e.error}`)
          .join("\n");
        const more =
          data.errors.length > CAP
            ? `\n  … ${t("importEdsp.morePre")}${data.errors.length - CAP}${t("importEdsp.moreSuf")}`
            : "";
        msg += `\n${t("importEdsp.errorsLabel")}:\n${sample}${more}`;
      }
      // F-comm-cleanup C4 — avisos não-fatais (linhas que caíram em legacy
      // por falta de defaults no técnico/cliente).
      if (data.warnings && data.warnings.length > 0) {
        const sample = data.warnings
          .slice(0, CAP)
          .map((w) => `  ⚠ L${w.lineNumber} (${w.numDevis}): ${w.message}`)
          .join("\n");
        const more =
          data.warnings.length > CAP
            ? `\n  … ${t("importEdsp.morePre")}${data.warnings.length - CAP}${t("importEdsp.moreSuf")}`
            : "";
        msg += `\n${t("importEdsp.warningsLabel")} (${data.warnings.length}):\n${sample}${more}`;
      }
      setImportResult(msg);
      // Re-roda preview para refletir o novo estado
      if (dossierCsv && companyId) {
        previewDossiersMut.mutate({ companyId, csvContent: dossierCsv });
      }
    },
    onError: (e) => setError(e.message),
  });

  async function handleDossierFile(file: File) {
    setError(null);
    setImportResult(null);
    try {
      // F-receipts-100pct + xlsx upload — converte XLSX→CSV browser-side
      const text = await readImportFileAsCsv(file);
      setDossierCsv(text);
      if (companyId) {
        previewDossiersMut.mutate({ companyId, csvContent: text });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  // ── F-repairs-import — Reparações (liste-orders): carros reparados ────────
  type RepairRowInput = {
    client: string;
    dateReparation: string | null;
    ordre: string;
    technician: string | null;
    ht: number;
    plate: string | null;
  };
  const [repairRows, setRepairRows] = useState<RepairRowInput[]>([]);
  const [repairsFileName, setRepairsFileName] = useState<string | null>(null);
  // F-repairs-fix-optin — correções de valores desligadas por padrão.
  const [repairApplyFixes, setRepairApplyFixes] = useState(false);
  const previewRepairsMut = trpc.edspImport.previewRepairs.useMutation({
    onError: (e) => setError(e.message),
  });
  const importRepairsMut = trpc.edspImport.importRepairs.useMutation({
    onSuccess: (data) => {
      let msg = `${t("importEdsp.repairsResultPre")}: ${data.summary.created} ${t("importEdsp.repairsCreatedWord")} · ${data.summary.fixed} ${t("importEdsp.repairsFixedWord")} · ${data.summary.skipped} ${t("importEdsp.alreadyExisted")} · ${data.summary.failed} ${t("importEdsp.failures")}.`;
      if (data.warnings.length > 0) {
        msg += `\n${t("importEdsp.warningsLabel")} (${data.warnings.length}):\n${data.warnings
          .map((w) => `  ⚠ ${w.ordre}: ${w.messages.join(" | ")}`)
          .join("\n")}`;
      }
      setImportResult(msg);
      if (repairRows.length > 0 && companyId) {
        previewRepairsMut.mutate({ companyId, rows: repairRows });
      }
    },
    onError: (e) => setError(e.message),
  });

  /** Data de célula xlsx: serial Excel (raw) ou texto — devolve YYYY-MM-DD. */
  function parseRepairDate(v: unknown): string | null {
    if (v == null || v === "") return null;
    if (typeof v === "number" && Number.isFinite(v)) {
      const d = new Date(Math.round((v - 25569) * 86400 * 1000));
      return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
    }
    const s = String(v);
    const iso = s.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const fr = s.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (fr) return `${fr[3]}-${fr[2]!.padStart(2, "0")}-${fr[1]!.padStart(2, "0")}`;
    return null;
  }

  async function handleRepairsFile(file: File) {
    setError(null);
    setImportResult(null);
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]!]!;
      const raw = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true }) as unknown[][];
      if (raw.length < 2) throw new Error(t("importEdsp.repairsEmptyFile"));
      const header = (raw[0] ?? []).map((h) =>
        String(h ?? "")
          .normalize("NFD")
          .replace(/[̀-ͯ]/g, "")
          .toLowerCase(),
      );
      const idx = (frag: string) => header.findIndex((h) => h.includes(frag));
      const iClient = idx("client");
      const iDate = idx("reparation");
      const iOrdre = idx("ordre");
      const iTech = idx("utilisateur");
      const iHt = idx("ht");
      const iPlate = idx("immat");
      if (iClient < 0 || iOrdre < 0 || iHt < 0) {
        throw new Error(t("importEdsp.repairsBadHeader"));
      }
      const rows: RepairRowInput[] = [];
      for (const r of raw.slice(1)) {
        const ordre = String(r[iOrdre] ?? "").trim();
        const client = String(r[iClient] ?? "").trim();
        if (!ordre || !client) continue;
        const htRaw = r[iHt];
        const ht =
          typeof htRaw === "number"
            ? htRaw
            : parseFloat(
                String(htRaw ?? "0")
                  .replace(/\s/g, "")
                  .replace(",", "."),
              );
        const tech = iTech >= 0 ? String(r[iTech] ?? "").trim() : "";
        const plate = iPlate >= 0 ? String(r[iPlate] ?? "").trim() : "";
        rows.push({
          client,
          dateReparation: iDate >= 0 ? parseRepairDate(r[iDate]) : null,
          ordre,
          technician: tech || null,
          ht: Number.isFinite(ht) ? ht : 0,
          plate: plate || null,
        });
      }
      if (rows.length === 0) throw new Error(t("importEdsp.repairsEmptyFile"));
      setRepairRows(rows);
      setRepairsFileName(file.name);
      if (companyId) previewRepairsMut.mutate({ companyId, rows });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  // ── F-edsp-devis-pdf (F1 handoff) — devis em PDF, lote ────────────────────
  const [devisFiles, setDevisFiles] = useState<Array<{ name: string; contentBase64: string }>>([]);
  const previewDevisMut = trpc.edspImport.previewDevisPdfs.useMutation({
    onError: (e) => setError(e.message),
  });
  const importDevisMut = trpc.edspImport.importDevisPdfs.useMutation();

  // F-devis-lotes (CEO 2026-09-27, "too_big · maximum 60") — o servidor
  // aceita no máximo 60 PDFs por chamada; a tela manda em LOTES e junta o
  // resultado numa tabela e numa mensagem só.
  const DEVIS_LOTE = 40;
  type DevisPreview = NonNullable<typeof previewDevisMut.data>;
  const [devisPreview, setDevisPreview] = useState<DevisPreview | null>(null);
  const [devisLote, setDevisLote] = useState<{
    fase: "preview" | "import";
    i: number;
    n: number;
  } | null>(null);
  function emLotes<T>(arr: T[]): T[][] {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += DEVIS_LOTE) out.push(arr.slice(i, i + DEVIS_LOTE));
    return out;
  }
  async function previewDevisEmLotes(files: Array<{ name: string; contentBase64: string }>) {
    if (!companyId || files.length === 0) return;
    const lotes = emLotes(files);
    const merged: DevisPreview = {
      rows: [],
      summary: { total: 0, new: 0, existing: 0, errors: 0 },
    };
    setDevisPreview(null);
    try {
      for (let i = 0; i < lotes.length; i++) {
        setDevisLote({ fase: "preview", i: i + 1, n: lotes.length });
        const r = await previewDevisMut.mutateAsync({ companyId, files: lotes[i]! });
        merged.rows.push(...r.rows);
        merged.summary.total += r.summary.total;
        merged.summary.new += r.summary.new;
        merged.summary.existing += r.summary.existing;
        merged.summary.errors += r.summary.errors;
      }
      setDevisPreview(merged);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDevisLote(null);
    }
  }
  async function importDevisEmLotes() {
    if (!companyId || devisFiles.length === 0) return;
    setImportResult(null);
    const lotes = emLotes(devisFiles);
    const tot = { imported: 0, enriched: 0, aligned: 0, skipped: 0, failed: 0 };
    const errors: Array<{ fileName: string; error: string }> = [];
    const warnings: Array<{ fileName: string; numDevis: string; message: string }> = [];
    try {
      for (let i = 0; i < lotes.length; i++) {
        setDevisLote({ fase: "import", i: i + 1, n: lotes.length });
        const r = await importDevisMut.mutateAsync({ companyId, files: lotes[i]! });
        tot.imported += r.summary.imported;
        tot.enriched += r.summary.enriched;
        tot.aligned += r.summary.aligned;
        tot.skipped += r.summary.skipped;
        tot.failed += r.summary.failed;
        errors.push(...r.errors);
        warnings.push(...r.warnings);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDevisLote(null);
    }
    let msg = `${t("importEdsp.devisImported")}: ${tot.imported} ${t("importEdsp.new")} · ${tot.enriched} ${t("importEdsp.devisEnrichedWord")} · ${tot.aligned} ${t("importEdsp.devisAlignedWord")} · ${tot.skipped} ${t("importEdsp.alreadyExisted")} · ${tot.failed} ${t("importEdsp.failures")}.`;
    if (errors.length > 0) {
      msg += `\n${t("importEdsp.errorsLabel")}:\n${errors
        .map((e) => `  • ${e.fileName}: ${e.error}`)
        .join("\n")}`;
    }
    if (warnings.length > 0) {
      msg += `\n${t("importEdsp.warningsLabel")} (${warnings.length}):\n${warnings
        .map((w) => `  ⚠ ${w.fileName} (${w.numDevis}): ${w.message}`)
        .join("\n")}`;
    }
    setImportResult(msg);
    await previewDevisEmLotes(devisFiles);
  }

  async function handleDevisPdfFiles(list: FileList) {
    setError(null);
    setImportResult(null);
    // Leitura POR ARQUIVO com tolerância: o Chrome falha com "file or
    // directory could not be found" quando o PDF foi selecionado de dentro
    // de um .zip aberto no Explorer, ainda está sincronizando (OneDrive) ou
    // foi movido após a seleção. Um arquivo ilegível não derruba o lote —
    // seguimos com os legíveis e listamos os que falharam.
    const files: Array<{ name: string; contentBase64: string }> = [];
    const unreadable: string[] = [];
    for (const file of Array.from(list)) {
      try {
        const buf = new Uint8Array(await file.arrayBuffer());
        let bin = "";
        const CHUNK = 0x8000;
        for (let i = 0; i < buf.length; i += CHUNK) {
          bin += String.fromCharCode(...buf.subarray(i, i + CHUNK));
        }
        files.push({ name: file.name, contentBase64: btoa(bin) });
      } catch {
        unreadable.push(file.name);
      }
    }
    if (unreadable.length > 0) {
      setError(
        `${t("importEdsp.devisUnreadablePre")} (${unreadable.length}): ${unreadable.join(", ")}. ${t("importEdsp.devisUnreadableHint")}`,
      );
    }
    setDevisFiles(files);
    await previewDevisEmLotes(files);
  }

  // ── F-edsp-customers-import — lista de clientes + contatos do eDSP ────────
  type CustomerRow = {
    name: string;
    legalName?: string;
    street?: string;
    postalCode?: string;
    city?: string;
    region?: string;
    email?: string;
    phone?: string;
    /** CEO 2026-11-01 — todos os contatos (o servidor limpa e grava). */
    contacts?: Array<{ name: string; role?: string; email?: string; phone?: string }>;
  };
  const [customerRows, setCustomerRows] = useState<CustomerRow[]>([]);
  const [customersFileName, setCustomersFileName] = useState<string | null>(null);
  const [contactsFileName, setContactsFileName] = useState<string | null>(null);
  const importCustomersMut = trpc.edspImport.importCustomers.useMutation({
    onError: (e) => setError(e.message),
  });
  const [customersPreview, setCustomersPreview] = useState<{
    summary: {
      total: number;
      created: number;
      enriched: number;
      unchanged: number;
      contacts: number;
    };
    results: Array<{ name: string; status: string; matchedName: string | null; contacts: number }>;
  } | null>(null);

  function sheetRows(file: ArrayBuffer): string[][] {
    const wb = XLSX.read(file, { type: "array", raw: false });
    const ws = wb.Sheets[wb.SheetNames[0]!]!;
    return XLSX.utils.sheet_to_json(ws, { header: 1, raw: false }) as string[][];
  }
  const normName = (v: string) =>
    v
      .toUpperCase()
      .replace(/\s+(SARL|SASU|SAS|EURL|SA)\s*$/i, "")
      .replace(/[^A-Z0-9]+/g, " ")
      .trim();

  async function handleCustomersFile(file: File) {
    setError(null);
    setCustomersPreview(null);
    const rows = sheetRows(await file.arrayBuffer());
    const header = (rows[0] ?? []).map((h) => (h ?? "").toString().trim().toLowerCase());
    const col = (label: string) => header.findIndex((h) => h.includes(label));
    const iName = col("nom usuel");
    const iLegal = col("raison");
    const iAddr = col("adresse");
    const iCp = col("cp");
    const iVille = col("ville");
    const iRegion = col("région") >= 0 ? col("région") : col("region");
    if (iName < 0) {
      setError(t("importEdsp.custBadHeader"));
      return;
    }
    const out: CustomerRow[] = [];
    for (const r of rows.slice(1)) {
      const name = (r[iName] ?? "").toString().trim();
      if (!name) continue;
      out.push({
        name,
        legalName: iLegal >= 0 ? (r[iLegal] ?? "").toString().trim() || undefined : undefined,
        street: iAddr >= 0 ? (r[iAddr] ?? "").toString().trim() || undefined : undefined,
        postalCode: iCp >= 0 ? (r[iCp] ?? "").toString().trim() || undefined : undefined,
        city: iVille >= 0 ? (r[iVille] ?? "").toString().trim() || undefined : undefined,
        region: iRegion >= 0 ? (r[iRegion] ?? "").toString().trim() || undefined : undefined,
      });
    }
    setCustomerRows(out);
    setCustomersFileName(file.name);
    // F-cust-import-ux (CEO 2026-09-16: "não encontrei o botão IMPORTAR") —
    // o preview roda sozinho ao escolher o arquivo; o botão Importar aparece
    // direto, sem precisar clicar em Pré-visualizar antes.
    void runCustomersImport(true, out);
  }

  async function handleContactsFile(file: File) {
    setError(null);
    setCustomersPreview(null);
    const rows = sheetRows(await file.arrayBuffer());
    const header = (rows[0] ?? []).map((h) => (h ?? "").toString().trim().toLowerCase());
    const iClient = header.findIndex((h) => h.includes("client"));
    const iEmail = header.findIndex((h) => h.includes("email"));
    const iTel = header.findIndex((h) => h.includes("téléphone") || h.includes("telephone"));
    const iNom = header.findIndex((h) => h === "nom");
    const iFonction = header.findIndex((h) => h.includes("fonction"));
    if (iClient < 0) {
      setError(t("importEdsp.custBadHeader"));
      return;
    }
    // CEO 2026-11-01 — todos os contatos de cada cliente vão para o servidor,
    // que tira os e-mails da casa ("DEVIS"), telefones falsos, e grava os
    // contatos no cadastro (antes só o 1º e-mail/telefone ia para o cliente).
    const byClient = new Map<
      string,
      Array<{ name: string; role?: string; email?: string; phone?: string }>
    >();
    const cel = (r: unknown[], i: number) =>
      i >= 0 ? (r[i] ?? "").toString().trim() || undefined : undefined;
    for (const r of rows.slice(1)) {
      const client = (r[iClient] ?? "").toString().trim();
      if (!client) continue;
      const key = normName(client);
      const lista = byClient.get(key) ?? [];
      lista.push({
        name: cel(r, iNom) ?? client,
        role: cel(r, iFonction),
        email: cel(r, iEmail),
        phone: cel(r, iTel),
      });
      byClient.set(key, lista);
    }
    // Contatos só habilitam depois do arquivo de clientes → customerRows já
    // reflete a lista carregada; merge local e re-preview automático.
    const merged = customerRows.map((c) => {
      const lista = byClient.get(normName(c.name));
      return lista ? { ...c, contacts: lista.slice(0, 50) } : c;
    });
    setCustomerRows(merged);
    setContactsFileName(file.name);
    void runCustomersImport(true, merged);
  }

  async function runCustomersImport(dryRun: boolean, rowsOverride?: CustomerRow[]) {
    const rows = rowsOverride ?? customerRows;
    if (!companyId || rows.length === 0) return;
    setError(null);
    const res = await importCustomersMut.mutateAsync({ companyId, dryRun, rows });
    setCustomersPreview(res);
    if (!dryRun) {
      setImportResult(
        `${t("importEdsp.custImported")}: ${res.summary.created} ${t("importEdsp.new")} · ${res.summary.enriched} ${t("importEdsp.custEnrichedWord")} · ${res.summary.unchanged} ${t("importEdsp.custUnchangedWord")} · ${res.summary.contacts} ${t("importEdsp.custContactsWord")}.`,
      );
    }
  }

  if (!user) return null;

  return (
    <ErpShell>
      <main className="max-w-7xl mx-auto p-6 space-y-6">
        <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 flex items-center gap-3 flex-wrap">
          <label className="text-sm text-neutral-400">{t("importEdsp.companyLabel")}</label>
          <select
            value={companyId ?? ""}
            onChange={(e) => setCompanyId(Number(e.target.value))}
            className="rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none w-72"
          >
            {accessibleCompanies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </section>

        {/* F-import-company-banner (CEO 2026-09-18, após o quase-incidente do
            Bonhomme na BS): TUDO nesta página entra na empresa selecionada —
            banner impossível de ignorar, sempre visível. */}
        {companyId != null && (
          <div
            className="sticky top-2 z-20 rounded-lg border-2 px-4 py-3 text-sm font-semibold shadow-lg"
            style={{ background: "#FDE68A", borderColor: "#D97706", color: "#78350F" }}
          >
            ⚠ {t("importEdsp.companyBannerPre")}{" "}
            <span
              className="rounded px-2 py-0.5 text-base font-bold uppercase"
              style={{ background: "#D97706", color: "#FFFFFF" }}
            >
              {accessibleCompanies.find((c) => c.id === companyId)?.name ?? companyId}
            </span>{" "}
            — {t("importEdsp.companyBannerSuf")}
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-500/10 border border-red-500/30 px-4 py-2 text-sm text-red-400 whitespace-pre-wrap">
            {error}
          </div>
        )}
        {importResult && (
          <div className="rounded-md bg-lime/10 border border-lime/30 px-4 py-2 text-sm text-lime whitespace-pre-wrap font-mono max-h-[420px] overflow-y-auto">
            {importResult}
          </div>
        )}

        {/* F-devis-email (CEO 2026-09-26) — devis do eDSP enviados por email. */}
        {companyId != null && <InboundDevisEmails companyId={companyId} />}

        {/* F-edsp-devis-pdf — Arquivo 3: DEVIS em PDF (antes da fatura) */}
        <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-semibold">{t("importEdsp.file3Title")}</h2>
              <p className="text-xs text-neutral-500 mt-1">{t("importEdsp.file3Hint")}</p>
            </div>
            {canEdit ? (
              <input
                type="file"
                accept=".pdf,application/pdf"
                multiple
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    void handleDevisPdfFiles(e.target.files);
                  }
                }}
                disabled={companyId === null}
                className="text-sm text-neutral-300 file:rounded-md file:border-0 file:bg-lime file:text-on-lime file:px-3 file:py-1.5 file:mr-3 file:hover:bg-lime-bright file:cursor-pointer"
              />
            ) : (
              <p className="text-sm text-neutral-500 italic">{t("importEdsp.readOnlyNote")}</p>
            )}
          </div>

          {devisLote && (
            <p className="text-sm text-neutral-500">
              {devisLote.fase === "preview"
                ? t("importEdsp.devisParsing")
                : t("importEdsp.importingLabel")}
              {devisLote.n > 1 &&
                ` — ${t("importEdsp.devisBatchPre")} ${devisLote.i} ${t("importEdsp.devisBatchOf")} ${devisLote.n}`}
            </p>
          )}

          {devisPreview && (
            <div className="space-y-3">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
                    <tr>
                      <th className="text-left py-1.5 pr-3">{t("importEdsp.devisColFile")}</th>
                      <th className="text-left py-1.5 pr-3">{t("importEdsp.devisColDevis")}</th>
                      <th className="text-left py-1.5 pr-3">{t("importEdsp.devisColDate")}</th>
                      <th className="text-left py-1.5 pr-3">{t("importEdsp.devisColClient")}</th>
                      <th className="text-left py-1.5 pr-3">{t("importEdsp.devisColPlate")}</th>
                      <th className="text-right py-1.5 pr-3">{t("importEdsp.devisColTotal")}</th>
                      <th className="text-left py-1.5">{t("importEdsp.devisColStatus")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800/60">
                    {devisPreview.rows.map((r) => (
                      <tr key={r.fileName}>
                        <td className="py-1.5 pr-3 font-mono text-xs">{r.fileName}</td>
                        <td className="py-1.5 pr-3 font-mono text-xs">{r.numDevis ?? "—"}</td>
                        <td className="py-1.5 pr-3 text-xs">{r.devisDate ?? "—"}</td>
                        <td className="py-1.5 pr-3 truncate max-w-[220px]">
                          {r.clientName ?? "—"}
                        </td>
                        <td className="py-1.5 pr-3 font-mono text-xs">{r.plate ?? "—"}</td>
                        <td className="py-1.5 pr-3 text-right font-mono tabular-nums">
                          {r.grandTotalHt != null
                            ? new Intl.NumberFormat("fr-FR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(r.grandTotalHt) + " €"
                            : "—"}
                        </td>
                        <td className="py-1.5">
                          <span
                            className={`inline-block rounded px-2 py-0.5 text-xs ${
                              r.status === "new"
                                ? "bg-lime/15 text-lime"
                                : r.status === "existing"
                                  ? r.willEnrich || r.willAlign
                                    ? "bg-amber-500/15 text-amber-300"
                                    : "bg-neutral-700/40 text-neutral-300"
                                  : "bg-red-500/15 text-red-300"
                            }`}
                            title={r.error ?? (r.warnings.join(" · ") || undefined)}
                          >
                            {r.status === "new"
                              ? t("importEdsp.devisStatusNew")
                              : r.status === "existing"
                                ? r.willAlign
                                  ? t("importEdsp.devisStatusAlign")
                                  : r.willEnrich
                                    ? t("importEdsp.devisStatusEnrich")
                                    : t("importEdsp.devisStatusExisting")
                                : t("importEdsp.devisStatusError")}
                            {r.warnings.length > 0 && " ⚠"}
                          </span>
                          {r.error && <p className="text-[11px] text-red-400 mt-0.5">{r.error}</p>}
                          {/* F-reimport-alinha — o que o import vai mudar (ou por que não). */}
                          {r.alignSummary && (
                            <p
                              className={`text-[11px] mt-0.5 ${r.alignBlocked ? "text-red-400" : "text-amber-300"}`}
                            >
                              {r.alignBlocked
                                ? `${t("importEdsp.devisAlignBlockedPre")} ${r.alignBlocked}`
                                : r.alignSummary}
                            </p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {canEdit &&
                (devisPreview.summary.new > 0 ||
                  devisPreview.rows.some((r) => r.willEnrich || r.willAlign)) && (
                  <button
                    onClick={() => void importDevisEmLotes()}
                    disabled={devisLote != null}
                    className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-2 text-sm font-medium"
                  >
                    {devisLote?.fase === "import"
                      ? t("importEdsp.importingLabel")
                      : `${t("importEdsp.devisImportBtn")} (${
                          devisPreview.summary.new +
                          devisPreview.rows.filter((r) => r.willEnrich || r.willAlign).length
                        })`}
                  </button>
                )}
            </div>
          )}
        </section>

        {/* F-edsp-doc-pdf — PDFs de faturas e avoirs emitidos pelo eDSP. */}
        {companyId != null && <EdspDocPdfs companyId={companyId} canEdit={canEdit} />}
        {companyId != null && <DevisPdfsAntigos companyId={companyId} canEdit={canEdit} />}

        {/* F-edsp-customers-import — Clientes (eDSP) */}
        <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-5 space-y-3">
          <div>
            <h2 className="text-lg font-semibold">{t("importEdsp.custTitle")}</h2>
            <p className="text-xs text-neutral-500 mt-1">{t("importEdsp.custHint")}</p>
          </div>
          {canEdit && (
            <div className="flex flex-wrap items-center gap-4">
              <label className="text-sm text-neutral-300">
                {t("importEdsp.custFileClients")}{" "}
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) void handleCustomersFile(f);
                  }}
                  className="text-sm text-neutral-300 file:rounded-md file:border-0 file:bg-lime file:text-on-lime file:px-3 file:py-1.5 file:mr-3 file:cursor-pointer"
                />
                {customersFileName && (
                  <span className="text-xs text-lime">✓ {customersFileName}</span>
                )}
              </label>
              <label className="text-sm text-neutral-300">
                {t("importEdsp.custFileContacts")}{" "}
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  disabled={customerRows.length === 0}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) void handleContactsFile(f);
                  }}
                  className="text-sm text-neutral-300 file:rounded-md file:border-0 file:bg-neutral-700 file:text-neutral-100 file:px-3 file:py-1.5 file:mr-3 file:cursor-pointer disabled:opacity-40"
                />
                {contactsFileName && (
                  <span className="text-xs text-lime">✓ {contactsFileName}</span>
                )}
              </label>
            </div>
          )}
          {customerRows.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-neutral-400">
                {customerRows.length} {t("importEdsp.custRowsWord")}
              </span>
              <button
                onClick={() => void runCustomersImport(true)}
                disabled={importCustomersMut.isPending}
                className="rounded-md border border-neutral-700 hover:border-lime px-3 py-1.5 text-sm disabled:opacity-50"
              >
                {t("importEdsp.custPreviewBtn")}
              </button>
              {customersPreview && (
                <button
                  onClick={() => void runCustomersImport(false)}
                  disabled={
                    importCustomersMut.isPending ||
                    customersPreview.summary.created + customersPreview.summary.enriched === 0
                  }
                  className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-4 py-1.5 text-sm font-medium disabled:opacity-50"
                >
                  {t("importEdsp.custImportBtn")} ({customersPreview.summary.created} +{" "}
                  {customersPreview.summary.enriched})
                </button>
              )}
            </div>
          )}
          {customersPreview && (
            <div className="max-h-72 overflow-y-auto rounded border border-neutral-800">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-neutral-950 text-xs uppercase tracking-wider text-neutral-500">
                  <tr>
                    <th className="px-3 py-1.5 text-left">{t("importEdsp.custColName")}</th>
                    <th className="px-3 py-1.5 text-left">{t("importEdsp.custColStatus")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800/60">
                  {customersPreview.results.map((r, i) => (
                    <tr key={i}>
                      <td className="px-3 py-1">{r.name}</td>
                      <td className="px-3 py-1">
                        <span
                          className={`rounded px-2 py-0.5 text-xs ${
                            r.status === "new"
                              ? "bg-lime/15 text-lime"
                              : r.status === "enriched"
                                ? "bg-amber-500/15 text-amber-300"
                                : "bg-neutral-700/40 text-neutral-300"
                          }`}
                        >
                          {r.status === "new"
                            ? t("importEdsp.custStatusNew")
                            : r.status === "enriched"
                              ? `${t("importEdsp.custStatusEnrich")}${r.matchedName ? ` (${r.matchedName})` : ""}`
                              : t("importEdsp.custStatusUnchanged")}
                        </span>
                        {r.contacts > 0 && (
                          <span className="ml-2 text-xs text-neutral-400">
                            +{r.contacts} {t("importEdsp.custContactsWord")}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Arquivo 1 — Dossiers / Faturas */}
        <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-semibold">{t("importEdsp.file1Title")}</h2>
              <p className="text-xs text-neutral-500 mt-1">
                {t("importEdsp.csvColumnsLabel")}{" "}
                <span className="font-mono">
                  Client; No de TVA; Date de facture; N° Facture; N° Devis; ...; Nom Technicien; HT
                  DSP; HT Démontage; Prix Net; ...
                </span>
              </p>
            </div>
            {canEdit ? (
              <input
                type="file"
                accept=".csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleDossierFile(f);
                }}
                disabled={companyId === null}
                className="text-sm text-neutral-300 file:rounded-md file:border-0 file:bg-lime file:text-on-lime file:px-3 file:py-1.5 file:mr-3 file:hover:bg-lime-bright file:cursor-pointer"
              />
            ) : (
              <p className="text-sm text-neutral-500 italic">{t("importEdsp.readOnlyNote")}</p>
            )}
          </div>

          {previewDossiersMut.isPending && (
            <p className="text-sm text-neutral-500">{t("importEdsp.parsingLabel")}</p>
          )}

          {dossierPreview && (
            <DossierPreviewBlock
              preview={dossierPreview}
              onImport={() => {
                if (dossierCsv && companyId) {
                  setImportResult(null);
                  importDossiersMut.mutate({
                    companyId,
                    csvContent: dossierCsv,
                  });
                }
              }}
              importing={importDossiersMut.isPending}
            />
          )}
        </section>

        {/* F-recebimentos-so-conciliacao (CEO 2026-09-30): recebimento do cliente
            entra SÓ pela conciliação bancária. O import de encaissements do eDSP
            criava pagamento sem dinheiro no banco (F2026-00035 BS: fatura
            inteira dada como paga com 15 000 € recebidos) e liberava técnicos. */}
        <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-5 space-y-2">
          <h2 className="text-lg font-semibold">{t("importEdsp.file2Title")}</h2>
          <p className="text-sm text-amber-300">🔒 {t("importEdsp.receiptsDisabledNote")}</p>
        </section>

        {/* ── F-repairs-import — Reparações (liste-orders) ── */}
        <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-semibold">🔧 {t("importEdsp.repairsTitle")}</h2>
              <p className="text-xs text-neutral-500 mt-1">{t("importEdsp.repairsHint")}</p>
            </div>
            {canEdit ? (
              <input
                type="file"
                accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleRepairsFile(f);
                  e.currentTarget.value = "";
                }}
                disabled={companyId === null}
                className="text-sm text-neutral-300 file:rounded-md file:border-0 file:bg-lime file:text-on-lime file:px-3 file:py-1.5 file:mr-3 file:hover:bg-lime-bright file:cursor-pointer"
              />
            ) : (
              <p className="text-sm text-neutral-500 italic">{t("importEdsp.readOnlyNote")}</p>
            )}
          </div>

          {previewRepairsMut.isPending && (
            <p className="text-sm text-neutral-500">{t("importEdsp.parsingLabel")}</p>
          )}

          {previewRepairsMut.data && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-xs">
                <span
                  className="rounded px-2 py-1"
                  style={{ background: "#16A34A", color: "#fff" }}
                >
                  {previewRepairsMut.data.summary.newCount} {t("importEdsp.repairsChipNew")}
                </span>
                <span
                  className="rounded px-2 py-1"
                  style={{ background: "#4B5563", color: "#fff" }}
                >
                  {previewRepairsMut.data.summary.existsCount} {t("importEdsp.repairsChipExists")}
                </span>
                {previewRepairsMut.data.summary.fixCount > 0 && (
                  <span
                    className="rounded px-2 py-1"
                    style={{ background: "#2563EB", color: "#fff" }}
                  >
                    {previewRepairsMut.data.summary.fixCount} {t("importEdsp.repairsChipFix")}
                  </span>
                )}
                {previewRepairsMut.data.summary.mismatchCount > 0 && (
                  <span
                    className="rounded px-2 py-1"
                    style={{ background: "#D97706", color: "#fff" }}
                  >
                    {previewRepairsMut.data.summary.mismatchCount}{" "}
                    {t("importEdsp.repairsChipMismatch")}
                  </span>
                )}
                {(previewRepairsMut.data.summary.plateConflictCount ?? 0) > 0 && (
                  <span
                    className="rounded px-2 py-1"
                    style={{ background: "#9333EA", color: "#fff" }}
                  >
                    {previewRepairsMut.data.summary.plateConflictCount}{" "}
                    {t("importEdsp.repairsChipPlateConflict")}
                  </span>
                )}
                {(previewRepairsMut.data.summary.noPlateCount ?? 0) > 0 && (
                  <span
                    className="rounded px-2 py-1"
                    style={{ background: "#0891B2", color: "#fff" }}
                  >
                    {previewRepairsMut.data.summary.noPlateCount}{" "}
                    {t("importEdsp.repairsChipNoPlate")}
                  </span>
                )}
                {previewRepairsMut.data.summary.errorCount > 0 && (
                  <span
                    className="rounded px-2 py-1"
                    style={{ background: "#DC2626", color: "#fff" }}
                  >
                    {previewRepairsMut.data.summary.errorCount} {t("importEdsp.failures")}
                  </span>
                )}
                {repairsFileName && <span className="text-neutral-500">{repairsFileName}</span>}
              </div>

              <div className="max-h-96 overflow-auto rounded border border-neutral-800">
                <table className="w-full text-xs">
                  <thead className="bg-neutral-950/50 text-neutral-500 uppercase tracking-wider sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5">{t("importEdsp.repairsColOrdre")}</th>
                      <th className="text-left px-2 py-1.5">{t("importEdsp.repairsColDate")}</th>
                      <th className="text-left px-2 py-1.5">{t("importEdsp.repairsColClient")}</th>
                      <th className="text-left px-2 py-1.5">{t("importEdsp.repairsColTech")}</th>
                      <th className="text-left px-2 py-1.5">{t("importEdsp.repairsColPlate")}</th>
                      <th className="text-right px-2 py-1.5">HT</th>
                      <th className="text-left px-2 py-1.5">{t("importEdsp.repairsColStatus")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800">
                    {previewRepairsMut.data.rows.map((r, i) => (
                      <tr key={i} className={r.status === "new" ? "" : "opacity-60"}>
                        <td className="px-2 py-1 font-mono">{r.ordre}</td>
                        <td className="px-2 py-1 tabular-nums">{r.dateReparation ?? "—"}</td>
                        <td className="px-2 py-1">{r.client}</td>
                        <td className="px-2 py-1">
                          {r.technician ?? "—"}
                          {r.willCreateTechnician && (
                            <span className="ml-1 text-amber-400">
                              ({t("importEdsp.repairsTagNewTech")})
                            </span>
                          )}
                          {r.status === "new" && r.noPair && !r.willCreateTechnician && (
                            <span className="ml-1 text-amber-400">
                              ({t("importEdsp.repairsTagNoPair")})
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1 font-mono">{r.plate ?? "—"}</td>
                        <td className="px-2 py-1 text-right font-mono tabular-nums">
                          {r.ht.toFixed(2)}
                        </td>
                        <td className="px-2 py-1">
                          {r.status === "new" && (
                            <span style={{ color: "#16A34A" }}>
                              {t("importEdsp.repairsStatusNew")}
                              {/* F-repairs-upgrade — completa o dossier do devis. */}
                              {r.upgradesDevisDossierCode && (
                                <span className="block">
                                  ↻ {t("importEdsp.repairsUpgradeDevisPre")}{" "}
                                  {r.upgradesDevisDossierCode}
                                </span>
                              )}
                            </span>
                          )}
                          {r.devisWithoutTechnician && (
                            <span className="block" style={{ color: "#D97706" }}>
                              ⚠ {t("importEdsp.repairsDevisNoTech")}
                            </span>
                          )}
                          {(r.status === "exists_ordre" || r.status === "exists_plate") && (
                            <span className="text-neutral-400">
                              {t("importEdsp.repairsStatusExists")}
                              {r.existingDossierCode ? ` · ${r.existingDossierCode}` : ""}
                            </span>
                          )}
                          {r.willFix && (
                            <span className="block" style={{ color: "#2563EB" }}>
                              ↻ {t("importEdsp.repairsWillFix")}
                            </span>
                          )}
                          {r.techMismatch && (
                            <span className="block" style={{ color: "#D97706" }}>
                              ⚠ {t("importEdsp.repairsTagMismatchPre")} {r.techMismatch}
                            </span>
                          )}
                          {r.plateConflict && (
                            <span className="block" style={{ color: "#9333EA" }}>
                              ⚠ {t("importEdsp.repairsTagPlateConflictPre")} {r.plateConflict}
                            </span>
                          )}
                          {/* F-repairs-vin — carro novo (só VIN): não entra por
                              aqui; mostra os dossiers VIN-only compatíveis. */}
                          {r.status === "no_plate" && (
                            <span className="block" style={{ color: "#0891B2" }}>
                              {t("importEdsp.repairsNoPlateMsg")}
                              {(r.vinCandidates ?? []).length > 0 && (
                                <span className="block text-neutral-400">
                                  {t("importEdsp.repairsNoPlateCandidatesPre")}{" "}
                                  {r.vinCandidates
                                    ?.map(
                                      (c) =>
                                        `${c.dossierCode ?? "?"} · ${c.vehicle} · VIN ${c.vin ?? "?"}`,
                                    )
                                    .join(" | ")}
                                </span>
                              )}
                            </span>
                          )}
                          {r.status === "error" && (
                            <span style={{ color: "#DC2626" }}>{r.error}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {canEdit &&
                previewRepairsMut.data.summary.newCount + previewRepairsMut.data.summary.fixCount >
                  0 && (
                  <div className="flex flex-wrap items-center gap-4">
                    <button
                      onClick={() => {
                        if (companyId && repairRows.length > 0) {
                          setImportResult(null);
                          importRepairsMut.mutate({
                            companyId,
                            rows: repairRows,
                            applyFixes: repairApplyFixes,
                          });
                        }
                      }}
                      disabled={importRepairsMut.isPending}
                      className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-4 py-2 text-sm font-medium disabled:opacity-40"
                    >
                      {importRepairsMut.isPending
                        ? t("importEdsp.importingLabel")
                        : `${t("importEdsp.repairsImportBtn")} (${
                            previewRepairsMut.data.summary.newCount +
                            (repairApplyFixes ? previewRepairsMut.data.summary.fixCount : 0)
                          })`}
                    </button>
                    {/* F-repairs-fix-optin — correção de valores só com confirmação
                        explícita: há ordens do eDSP chiffrées JÁ com a remise
                        (HT do arquivo = líquido); só o devis distingue. */}
                    {previewRepairsMut.data.summary.fixCount > 0 && (
                      <label
                        className="flex items-center gap-2 text-xs"
                        style={{ color: "#2563EB" }}
                      >
                        <input
                          type="checkbox"
                          checked={repairApplyFixes}
                          onChange={(e) => setRepairApplyFixes(e.target.checked)}
                        />
                        ↻ {t("importEdsp.repairsApplyFixesPre")} (
                        {previewRepairsMut.data.summary.fixCount}) —{" "}
                        {t("importEdsp.repairsApplyFixesSuf")}
                      </label>
                    )}
                  </div>
                )}
            </div>
          )}
        </section>
      </main>
    </ErpShell>
  );
}

function DossierPreviewBlock({
  preview,
  onImport,
  importing,
}: {
  preview: DossierPreview;
  onImport: () => void;
  importing: boolean;
}) {
  const t = useT();
  const { rows, parseErrors, summary } = preview;
  const newRows = rows.filter((r) => r.status === "new");
  const existing = rows.filter((r) => r.status === "existing");

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="rounded px-2 py-0.5 text-xs bg-lime/15 text-lime">
          {newRows.length} {t("importEdsp.new")}
        </span>
        <span className="rounded px-2 py-0.5 text-xs bg-neutral-700/40 text-neutral-300">
          {existing.length} {t("importEdsp.existLabel")}
        </span>
        {parseErrors.length > 0 && (
          <span className="rounded px-2 py-0.5 text-xs bg-red-500/15 text-red-300">
            {parseErrors.length} {t("importEdsp.parseErrorsBadge")}
          </span>
        )}
        <div className="flex-1" />
        <button
          onClick={onImport}
          disabled={importing || newRows.length === 0}
          className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-4 py-1.5 text-sm font-medium"
        >
          {importing
            ? t("importEdsp.importingLabel")
            : `${t("importEdsp.importAction")} ${newRows.length} ${t("importEdsp.dossierWord")}${newRows.length === 1 ? "" : "s"}`}
        </button>
      </div>

      <div className="rounded-lg border border-neutral-800 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-neutral-950/50 text-neutral-500 uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 w-20">{t("importEdsp.statusCol")}</th>
              <th className="text-left px-3 py-2 w-32">N° Devis</th>
              <th className="text-left px-3 py-2 w-32">N° Facture</th>
              <th className="text-left px-3 py-2">{t("importEdsp.clientCol")}</th>
              <th className="text-left px-3 py-2 w-32">{t("importEdsp.technicianCol")}</th>
              <th className="text-right px-3 py-2 w-28 font-mono">Prix Net</th>
              <th className="text-right px-3 py-2 w-24 font-mono">HT DSP</th>
              <th className="text-right px-3 py-2 w-28 font-mono">HT Démontage</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {rows.map((r) => (
              <tr key={r.lineNumber}>
                <td className="px-3 py-1.5">
                  <span
                    className={`inline-block rounded px-1.5 py-0.5 text-xs ${
                      r.status === "new"
                        ? "bg-lime/15 text-lime"
                        : r.status === "existing"
                          ? "bg-neutral-700/40 text-neutral-400"
                          : "bg-red-500/15 text-red-300"
                    }`}
                  >
                    {r.status}
                  </span>
                </td>
                <td className="px-3 py-1.5 font-mono text-neutral-300">
                  {r.parsed.numDevis}
                  {/* F-trava-duplicata — vai faturar o dossier em aberto do carro. */}
                  {r.openDossierId != null && (
                    <span className="mt-0.5 block font-sans text-[11px] text-amber-300">
                      → {r.openDossierCode ?? `#${r.openDossierId}`} (
                      {t("importEdsp.openDossierTag")})
                    </span>
                  )}
                </td>
                <td className="px-3 py-1.5 font-mono text-neutral-500">
                  {r.parsed.numFacture ?? "—"}
                </td>
                <td className="px-3 py-1.5">{r.parsed.client}</td>
                <td className="px-3 py-1.5 text-neutral-400">{r.parsed.nomTechnicien}</td>
                <td className="px-3 py-1.5 text-right font-mono">
                  {formatMoney(r.parsed.prixNet, { decimals: 2 })}
                </td>
                <td className="px-3 py-1.5 text-right font-mono text-neutral-500">
                  {r.parsed.htDsp > 0 ? formatMoney(r.parsed.htDsp, { decimals: 2 }) : "—"}
                </td>
                <td className="px-3 py-1.5 text-right font-mono text-neutral-500">
                  {r.parsed.htDemontage > 0
                    ? formatMoney(r.parsed.htDemontage, { decimals: 2 })
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {parseErrors.length > 0 && (
        <details className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-xs" open>
          <summary className="cursor-pointer text-red-300 font-medium">
            {parseErrors.length} {t("importEdsp.linesParseErrorExpand")}
          </summary>
          <div className="mt-3 space-y-3">
            {parseErrors.slice(0, 10).map((e) => (
              <div key={e.lineNumber} className="border border-red-500/20 rounded p-2 bg-red-500/5">
                <div className="font-mono text-red-300">
                  {t("importEdsp.lineLabel")} {e.lineNumber}: {e.error}
                </div>
                <div className="font-mono text-neutral-400 mt-1 text-[10px] break-all">
                  raw: {e.rawLine || t("importEdsp.emptyRaw")}
                </div>
              </div>
            ))}
            {parseErrors.length > 10 && (
              <p className="text-red-300/70 italic">
                … +{parseErrors.length - 10} {t("importEdsp.omitted")}
              </p>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

/**
 * F-devis-email (CEO 2026-09-26) — o técnico envia o devis pelo eDSP, a caixa
 * atual encaminha para o endereço de recebimento e o Gestion cria o devis
 * sozinho. Aqui o escritório vê o endereço e o que chegou (e o que falhou).
 */
function InboundDevisEmails({ companyId }: { companyId: number }) {
  const t = useT();
  const q = trpc.edspImport.inboundDevisEmails.useQuery({ companyId }, { refetchInterval: 30_000 });
  const rows = q.data?.rows ?? [];
  const badge: Record<string, [string, string]> = {
    imported: ["b-ok", t("importEdsp.mailStImported")],
    enriched: ["b-info", t("importEdsp.mailStEnriched")],
    updated: ["b-ok", t("importEdsp.mailStUpdated")],
    duplicate: ["b-mut", t("importEdsp.mailStDuplicate")],
    review: ["b-warn", t("importEdsp.mailStReview")],
    error: ["b-dang", t("importEdsp.mailStError")],
    ignored: ["b-mut", t("importEdsp.mailStIgnored")],
    processing: ["b-info", t("importEdsp.mailStProcessing")],
  };
  const muted = { color: "var(--text-muted)" };
  return (
    <section
      className="rounded-lg border border-neutral-800 bg-neutral-900 p-5 space-y-3"
      data-testid="inbound-devis-emails"
    >
      <div>
        <h2 className="text-lg font-semibold">{t("importEdsp.mailTitle")}</h2>
        <p className="text-xs mt-1" style={muted}>
          {t("importEdsp.mailHint")}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <span style={muted}>{t("importEdsp.mailAddress")}</span>
        {q.data?.address ? (
          <code
            className="rounded px-2 py-0.5 font-mono text-sm select-all"
            style={{ background: "var(--surface-muted)" }}
          >
            {q.data.address}
          </code>
        ) : (
          <span className="badge b-warn">{t("importEdsp.mailNoAddress")}</span>
        )}
        {q.data && !q.data.enabled && (
          <span className="badge b-dang">{t("importEdsp.mailDisabled")}</span>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm" style={muted}>
          {t("importEdsp.mailEmpty")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider" style={muted}>
                <th className="py-1.5 pr-3">{t("importEdsp.mailColWhen")}</th>
                <th className="py-1.5 pr-3">{t("importEdsp.mailColFrom")}</th>
                <th className="py-1.5 pr-3">{t("importEdsp.mailColDevis")}</th>
                <th className="py-1.5 pr-3">{t("importEdsp.mailColStatus")}</th>
                <th className="py-1.5">{t("importEdsp.mailColDetail")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const [cls, label] = badge[r.status] ?? ["b-mut", r.status];
                const devis = `${r.numDevis ?? r.attachmentName ?? "—"}${r.plate ? ` · ${r.plate}` : ""}`;
                return (
                  <tr key={r.id} className="border-t border-neutral-800 align-top">
                    <td className="py-1.5 pr-3 whitespace-nowrap tabular-nums">
                      {new Date(r.receivedAt).toLocaleString("fr-FR", {
                        day: "2-digit",
                        month: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="py-1.5 pr-3 max-w-[220px] truncate" title={r.subject ?? ""}>
                      {r.fromAddress ?? "—"}
                    </td>
                    <td className="py-1.5 pr-3 whitespace-nowrap font-mono">
                      {r.dossierId ? (
                        <a href={`/dossiers/${r.dossierId}`} className="underline">
                          {devis}
                        </a>
                      ) : (
                        devis
                      )}
                    </td>
                    <td className="py-1.5 pr-3">
                      <span className={`badge ${cls}`}>{label}</span>
                    </td>
                    <td className="py-1.5 text-xs min-w-[280px]" style={muted}>
                      {r.message ?? ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * F-edsp-doc-pdf (CEO 2026-09-27) — faturas e avoirs emitidos pelo eDSP são
 * o documento que o cliente recebeu: o Gestion confere os valores e anexa o
 * PDF ORIGINAL à fatura/avoir que já existe (o Gestion só gera PDF do que ele
 * mesmo emite). Em lotes de 40, com prévia antes de anexar.
 */
/**
 * CEO 2026-10-26 — "Anexar PDFs antigos (só guardar)": devis importados antes
 * de o Gestion guardar o arquivo. Baixados de novo do eDSP, cada PDF vai para
 * o dossier do mesmo nº de devis + placa, sem reimportar (valores intocados).
 */
function DevisPdfsAntigos({ companyId, canEdit }: { companyId: number; canEdit: boolean }) {
  const t = useT();
  const mut = trpc.edspImport.attachDevisOriginals.useMutation();
  type Linha = NonNullable<typeof mut.data>["rows"][number];
  const [files, setFiles] = useState<Array<{ name: string; contentBase64: string }>>([]);
  const [linhas, setLinhas] = useState<Linha[] | null>(null);
  const [lote, setLote] = useState<{ fase: "preview" | "attach"; i: number; n: number } | null>(
    null,
  );
  const [erro, setErro] = useState<string | null>(null);
  const LOTE = 30;
  async function rodar(fs: Array<{ name: string; contentBase64: string }>, dryRun: boolean) {
    const lotes: Array<typeof fs> = [];
    for (let i = 0; i < fs.length; i += LOTE) lotes.push(fs.slice(i, i + LOTE));
    const out: Linha[] = [];
    setErro(null);
    try {
      for (let i = 0; i < lotes.length; i++) {
        setLote({ fase: dryRun ? "preview" : "attach", i: i + 1, n: lotes.length });
        const r = await mut.mutateAsync({ companyId, dryRun, files: lotes[i]! });
        out.push(...r.rows);
      }
      setLinhas(out);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setLote(null);
    }
  }
  async function escolher(list: FileList) {
    const lidos: Array<{ name: string; contentBase64: string }> = [];
    for (const file of Array.from(list)) {
      try {
        const buf = new Uint8Array(await file.arrayBuffer());
        let bin = "";
        for (let i = 0; i < buf.length; i += 0x8000) {
          bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
        }
        lidos.push({ name: file.name, contentBase64: btoa(bin) });
      } catch {
        // arquivo ilegível: segue com os demais
      }
    }
    setFiles(lidos);
    setLinhas(null);
    if (lidos.length) await rodar(lidos, true);
  }
  const aAnexar = (linhas ?? []).filter((l) => l.status === "attach").length;
  const selo: Record<string, [string, string]> = {
    attach: ["b-info", t("importEdsp.oldPdfStAttach")],
    attached: ["b-ok", t("importEdsp.oldPdfStAttached")],
    already: ["b-mut", t("importEdsp.oldPdfStAlready")],
    not_found: ["b-warn", t("importEdsp.oldPdfStNotFound")],
    ambiguous: ["b-dang", t("importEdsp.oldPdfStAmbiguous")],
    unreadable: ["b-mut", t("importEdsp.oldPdfStUnreadable")],
  };
  return (
    <section
      className="rounded-lg border border-neutral-800 bg-neutral-900 p-5 space-y-3"
      data-testid="devis-old-pdfs"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold">{t("importEdsp.oldPdfTitle")}</h2>
          <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
            {t("importEdsp.oldPdfHint")}
          </p>
        </div>
        {canEdit && (
          <input
            type="file"
            accept=".pdf,application/pdf"
            multiple
            data-testid="devis-old-pdfs-input"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) void escolher(e.target.files);
            }}
            className="text-sm text-neutral-300 file:rounded-md file:border-0 file:bg-lime file:text-on-lime file:px-3 file:py-1.5 file:mr-3 file:hover:bg-lime-bright file:cursor-pointer"
          />
        )}
      </div>
      {lote && (
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          {lote.fase === "preview" ? t("importEdsp.devisParsing") : t("importEdsp.importingLabel")}
          {lote.n > 1 &&
            ` — ${t("importEdsp.devisBatchPre")} ${lote.i} ${t("importEdsp.devisBatchOf")} ${lote.n}`}
        </p>
      )}
      {erro && <p className="text-sm text-red-400">{erro}</p>}
      {linhas && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-left text-xs uppercase tracking-wider"
                  style={{ color: "var(--text-muted)" }}
                >
                  <th className="py-1.5 pr-3">{t("importEdsp.devisColFile")}</th>
                  <th className="py-1.5 pr-3">{t("importEdsp.oldPdfColDevis")}</th>
                  <th className="py-1.5 pr-3">{t("importEdsp.devisColPlate")}</th>
                  <th className="py-1.5 pr-3">{t("importEdsp.oldPdfColDossier")}</th>
                  <th className="py-1.5 pr-3">{t("importEdsp.devisColStatus")}</th>
                  <th className="py-1.5">{t("importEdsp.mailColDetail")}</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => {
                  const [cls, label] = selo[l.status] ?? ["b-mut", l.status];
                  return (
                    <tr key={l.fileName} className="border-t border-neutral-800 align-top">
                      <td className="py-1.5 pr-3 font-mono text-xs">{l.fileName}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs whitespace-nowrap">
                        {l.numDevis ?? "—"}
                      </td>
                      <td className="py-1.5 pr-3 font-mono text-xs whitespace-nowrap">
                        {l.plate ?? "—"}
                      </td>
                      <td className="py-1.5 pr-3 font-mono text-xs whitespace-nowrap">
                        {l.dossierId != null ? (
                          <a
                            href={`/dossiers/${l.dossierId}`}
                            className="hover:text-lime underline"
                          >
                            {l.dossierCode}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-1.5 pr-3">
                        <span className={`badge ${cls} whitespace-nowrap`}>{label}</span>
                      </td>
                      <td className="py-1.5 text-xs" style={{ color: "var(--text-muted)" }}>
                        {l.message === "placa" ? t("importEdsp.oldPdfViaPlate") : (l.message ?? "")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {canEdit && aAnexar > 0 && (
            <button
              onClick={() =>
                void rodar(
                  files.filter((f) =>
                    linhas.some((l) => l.fileName === f.name && l.status === "attach"),
                  ),
                  false,
                ).then(() => rodar(files, true))
              }
              disabled={lote != null}
              className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-2 text-sm font-medium"
              data-testid="devis-old-pdfs-attach"
            >
              {t("importEdsp.oldPdfAttachBtn")} ({aAnexar})
            </button>
          )}
        </>
      )}
    </section>
  );
}

function EdspDocPdfs({ companyId, canEdit }: { companyId: number; canEdit: boolean }) {
  const t = useT();
  const mut = trpc.edspImport.edspDocPdfs.useMutation();
  type Linha = NonNullable<typeof mut.data>["rows"][number];
  const [files, setFiles] = useState<Array<{ name: string; contentBase64: string }>>([]);
  const [linhas, setLinhas] = useState<Linha[] | null>(null);
  const [lote, setLote] = useState<{ fase: "preview" | "attach"; i: number; n: number } | null>(
    null,
  );
  const [erro, setErro] = useState<string | null>(null);
  const LOTE = 40;
  async function rodar(fs: Array<{ name: string; contentBase64: string }>, dryRun: boolean) {
    const lotes: Array<typeof fs> = [];
    for (let i = 0; i < fs.length; i += LOTE) lotes.push(fs.slice(i, i + LOTE));
    const out: Linha[] = [];
    setErro(null);
    try {
      for (let i = 0; i < lotes.length; i++) {
        setLote({ fase: dryRun ? "preview" : "attach", i: i + 1, n: lotes.length });
        const r = await mut.mutateAsync({ companyId, dryRun, files: lotes[i]! });
        out.push(...r.rows);
      }
      // F-edsp-doc-dup — o mesmo documento selecionado 2× (ex.: "A2026-0005.pdf"
      // e "A2026-0005 (1).pdf", baixados de novo do eDSP): só o 1º conta.
      const vistos = new Map<string, string>();
      for (const l of out) {
        if (!l.number || l.status === "unreadable") continue;
        const primeiro = vistos.get(l.number);
        if (primeiro == null) {
          vistos.set(l.number, l.fileName);
        } else if (l.status === "attach") {
          l.status = "duplicate";
          l.message = `${t("importEdsp.docDupPre")} ${primeiro}`;
        }
      }
      setLinhas(out);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setLote(null);
    }
  }
  async function escolher(list: FileList) {
    const lidos: Array<{ name: string; contentBase64: string }> = [];
    for (const file of Array.from(list)) {
      try {
        const buf = new Uint8Array(await file.arrayBuffer());
        let bin = "";
        for (let i = 0; i < buf.length; i += 0x8000) {
          bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
        }
        lidos.push({ name: file.name, contentBase64: btoa(bin) });
      } catch {
        // arquivo ilegível: segue com os demais
      }
    }
    setFiles(lidos);
    setLinhas(null);
    if (lidos.length) await rodar(lidos, true);
  }
  const aAnexar = (linhas ?? []).filter((l) => l.status === "attach").length;
  const selo: Record<string, [string, string]> = {
    attach: ["b-info", t("importEdsp.docStAttach")],
    attached: ["b-ok", t("importEdsp.docStAttached")],
    already: ["b-mut", t("importEdsp.docStAlready")],
    mismatch: ["b-dang", t("importEdsp.docStMismatch")],
    not_found: ["b-warn", t("importEdsp.docStNotFound")],
    unreadable: ["b-mut", t("importEdsp.docStUnreadable")],
    duplicate: ["b-mut", t("importEdsp.docStDuplicate")],
  };
  const m2 = (n: number | null) => (n == null ? "—" : formatMoney(n, { decimals: 2 }));
  return (
    <section
      className="rounded-lg border border-neutral-800 bg-neutral-900 p-5 space-y-3"
      data-testid="edsp-doc-pdfs"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold">{t("importEdsp.docTitle")}</h2>
          <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
            {t("importEdsp.docHint")}
          </p>
        </div>
        {canEdit && (
          <input
            type="file"
            accept=".pdf,application/pdf"
            multiple
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) void escolher(e.target.files);
            }}
            className="text-sm text-neutral-300 file:rounded-md file:border-0 file:bg-lime file:text-on-lime file:px-3 file:py-1.5 file:mr-3 file:hover:bg-lime-bright file:cursor-pointer"
          />
        )}
      </div>
      {lote && (
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          {lote.fase === "preview" ? t("importEdsp.devisParsing") : t("importEdsp.importingLabel")}
          {lote.n > 1 &&
            ` — ${t("importEdsp.devisBatchPre")} ${lote.i} ${t("importEdsp.devisBatchOf")} ${lote.n}`}
        </p>
      )}
      {erro && <p className="text-sm text-red-400">{erro}</p>}
      {linhas && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-left text-xs uppercase tracking-wider"
                  style={{ color: "var(--text-muted)" }}
                >
                  <th className="py-1.5 pr-3">{t("importEdsp.devisColFile")}</th>
                  <th className="py-1.5 pr-3">{t("importEdsp.docColDoc")}</th>
                  <th className="py-1.5 pr-3">{t("importEdsp.devisColPlate")}</th>
                  <th className="py-1.5 pr-3 text-right">{t("importEdsp.docColPdfHt")}</th>
                  <th className="py-1.5 pr-3 text-right">{t("importEdsp.docColGestionHt")}</th>
                  <th className="py-1.5 pr-3">{t("importEdsp.devisColStatus")}</th>
                  <th className="py-1.5">{t("importEdsp.mailColDetail")}</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => {
                  const [cls, label] = selo[l.status] ?? ["b-mut", l.status];
                  return (
                    <tr key={l.fileName} className="border-t border-neutral-800 align-top">
                      <td className="py-1.5 pr-3 font-mono text-xs">{l.fileName}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs whitespace-nowrap">
                        {l.number ?? "—"}
                      </td>
                      <td className="py-1.5 pr-3 font-mono text-xs">{l.plate ?? "—"}</td>
                      <td className="py-1.5 pr-3 text-right font-mono tabular-nums whitespace-nowrap">
                        {m2(l.pdfHt)}
                      </td>
                      <td className="py-1.5 pr-3 text-right font-mono tabular-nums whitespace-nowrap">
                        {m2(l.gestionHt)}
                      </td>
                      <td className="py-1.5 pr-3">
                        <span className={`badge ${cls}`}>{label}</span>
                      </td>
                      <td className="py-1.5 text-xs" style={{ color: "var(--text-muted)" }}>
                        {l.message ?? ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {canEdit && aAnexar > 0 && (
            <button
              onClick={() =>
                void rodar(
                  files.filter((f) =>
                    linhas.some((l) => l.fileName === f.name && l.status === "attach"),
                  ),
                  false,
                ).then(() => rodar(files, true))
              }
              disabled={lote != null}
              className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-2 text-sm font-medium"
              data-testid="edsp-doc-attach"
            >
              {t("importEdsp.docAttachBtn")} ({aAnexar})
            </button>
          )}
        </>
      )}
    </section>
  );
}
